import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/utils';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Klik do zašedlého backdropu zavírá. Default `true`. */
  closeOnBackdrop?: boolean;
  /** Accessible label panelu (`aria-label`). */
  ariaLabel?: string;
  /** Extra třídy na panel (šířku řídí `width`). */
  className?: string;
  /**
   * Klidová šířka panelu. Vždy se ořízne do rozmezí min 400 px … max 2/3 okna,
   * takže na úzkém okně neklesne pod 400 px a na širokém nepřeroste 2/3.
   * Default `560px`.
   */
  width?: string;
}

/**
 * Phoenix side drawer — sdílená modální komponenta vyjíždějící zprava přes celou
 * výšku okna. Zrcadlí mechanismus `Dialog` (portal na `document.body`, zašedlý
 * backdrop, zavření na Escape / klik do backdropu, focus panelu), navíc přidává
 * slide-in/out animaci přes `data-open` (viz `.phx-drawer*` v `index.css`).
 *
 * Komponenta zůstává mountnutá i během zavírací animace — po doznění slide-outu
 * (`transitionend` na `transform`) se teprve odmountuje.
 */
export function Drawer({
  open,
  onClose,
  children,
  closeOnBackdrop = true,
  ariaLabel,
  className,
  width = '560px',
}: DrawerProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  // `rendered` drží portal v DOM i během zavíracího slide-outu; `shown` spouští
  // CSS transition (na další frame po mountu → přehraje se enter animace).
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (shown && surfaceRef.current) {
      surfaceRef.current.focus();
    }
  }, [shown]);

  if (!rendered || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      // Sdílený overlay token `--phx-z-overlay` — nad okny PhoenixDesktopu (z-index roste neomezeně).
      style={{ zIndex: 'var(--phx-z-overlay)' }}
      className="phx-drawer-root fixed inset-0 flex justify-end"
    >
      <div
        aria-hidden
        onClick={closeOnBackdrop ? onClose : undefined}
        data-open={shown}
        className="phx-drawer-backdrop bg-phx-backdrop absolute inset-0 backdrop-blur-[1px]"
      />
      <div
        ref={surfaceRef}
        tabIndex={-1}
        data-open={shown}
        onTransitionEnd={(event) => {
          if (!open && event.target === event.currentTarget && event.propertyName === 'transform') {
            setRendered(false);
          }
        }}
        style={{ width: `clamp(400px, ${width}, 66.67vw)` }}
        className={cn(
          'phx-drawer bg-phx-surface text-phx-text border-phx-border shadow-phx-lg relative z-10 flex h-full max-w-full flex-col border-l outline-none',
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
